import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["avatar", "buttonType", "children", "chipElevation", "disabled", "icon", "iconPosition", "iconSize", "inverse", "fluid", "loading", "loadingVoiceOver", "size"];
import PropTypes from 'prop-types';
import React, { cloneElement, forwardRef } from 'react';
import { StyledAssistChip } from './AssistChip.styles';
import { ChipLabel } from './ChipLabel';
import { ChipSpinner } from './ChipSpinner';

/**
 * Assist chips are used to trigger actions related to pieces of content.
 * Avoid using them as CTAs or for navigation (ex: as tabs).
 *
 * @extends Button
 * @related ['FilterChip', 'InputChip', 'Tag']
 * @since 8.97.0
 */
export var AssistChip = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var avatar = props.avatar,
    buttonType = props.buttonType,
    children = props.children,
    chipElevation = props.chipElevation,
    disabled = props.disabled,
    icon = props.icon,
    iconPosition = props.iconPosition,
    iconSize = props.iconSize,
    inverse = props.inverse,
    fluid = props.fluid,
    loading = props.loading,
    loadingVoiceOver = props.loadingVoiceOver,
    size = props.size,
    rest = _objectWithoutProperties(props, _excluded);
  var adjustedAvatar;
  if (avatar) {
    // Prevent img alt text from being read and
    // chip from being announced as a "button, group"
    adjustedAvatar = /*#__PURE__*/cloneElement(avatar, {
      'aria-hidden': 'true',
      'as': 'span'
    });
  }
  return /*#__PURE__*/React.createElement(StyledAssistChip, _extends({
    buttonType: "tertiary",
    chipElevation: chipElevation,
    disabled: disabled,
    fluid: false,
    icon: icon,
    iconPosition: "start",
    iconSize: "xs",
    inverse: false,
    loading: loading,
    ref: forwardedRef,
    type: "button"
  }, rest), loading ? /*#__PURE__*/React.createElement(ChipSpinner, {
    loadingVoiceOver: loadingVoiceOver
  }) : null, avatar ? adjustedAvatar : null, /*#__PURE__*/React.createElement(ChipLabel, null, children));
});
AssistChip.displayName = 'AssistChip';
AssistChip.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Adds a leading avatar. Takes an `Avatar` component. Ignores the `Avatar`’s `size` prop
   * (i.e. the avatar size is fixed).
   */
  avatar: PropTypes.node,
  /**
   * The assist chip’s text label. To add decorators, use the `icon` and `avatar` props.
   */
  children: PropTypes.node.isRequired,
  /**
   * When true, adds a drop shadow. Useful for making the assist chip easier to see on
   * certain backgrounds (ex: on top of a photo or map)
   */
  chipElevation: PropTypes.bool,
  /**
   * Sets the assist chip as disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Adds a leading icon. Takes an `Icon` component but ignores its `size` prop
   * (i.e. the icon size is fixed). Does not support `DetailedIcon`.
   */
  icon: PropTypes.node,
  /**
   * When true, displays the chip in its loading state. This adds a spinner next to the
   * label text and disables pointer events. The chip is not fully disabled so it can still
   * retain focus.
   */
  loading: PropTypes.bool,
  /**
   * The text that will be announced to screen readers when the chip changes to
   * its loading state. Defaults to "loading". Text is `VisuallyHidden`.
   *
   * Don't include the chip's label text in `loadingVoiceOver` because some assistive
   * technologies will, by default, read the label text after the `loadingVoiceOver` text.
   */
  loadingVoiceOver: PropTypes.string
} : {};
AssistChip.defaultProps = {
  avatar: null,
  chipElevation: false,
  disabled: false,
  icon: null,
  loading: false,
  loadingVoiceOver: 'Loading'
};